import {LitElement, html, css, type CSSResultGroup} from 'lit';
import {SignalWatcher} from '@lit-labs/preact-signals';
import {env} from '../../config/env.js';
import {authedFetch} from '../../auth/auth-fetch.js';
import {rolesSignal} from '../../services/permission.service.js';
import {loadAdminUsers, resolveOrgId, type AdminUser} from '../../services/admin-users.service.js';
import {morphedCreate} from '../../services/morphed-entity.service.js';

interface Toast {kind: 'ok' | 'err' | 'info'; text: string}

export class ClamUserManager extends SignalWatcher(LitElement) {
    static styles: CSSResultGroup = css`
        :host { display: block; font-family: var(--clam-font); color: var(--clam-text); }

        .toolbar { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; padding: var(--clam-space-4, 16px); gap: var(--clam-space-3, 12px); }
        .toolbar__left { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .toolbar__title { font-size: var(--clam-text-xl, 20px); font-weight: 700; color: var(--clam-darkgray, #424244); margin: 0; }
        .toolbar__subtitle { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); margin: 0; }
        .toolbar__right { display: flex; align-items: center; gap: var(--clam-space-2, 8px); flex-wrap: wrap; }

        .btn { padding: 7px var(--clam-space-4, 16px); border-radius: var(--clam-radius-sm, 6px); border: none; cursor: pointer; font-size: var(--clam-text-sm, 13px); font-weight: 500; font-family: var(--clam-font); transition: background 0.15s; white-space: nowrap; }
        .btn-primary { background: var(--clam-light-green, #6bbb24); color: var(--clam-white, #fff); }
        .btn-primary:hover { background: var(--clam-green, #5a9d1e); }
        .btn-danger { background: var(--clam-danger, #ea4335); color: var(--clam-white, #fff); }
        .btn-danger:hover { background: #c83227; }
        .btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-sm { padding: 6px 12px; font-size: var(--clam-text-xs, 11px); min-height: 34px; }
        .btn-secondary { background: var(--clam-surface, #fff); color: var(--clam-text, #333); border: 1px solid var(--clam-border, #e2e8f0); }
        .btn-secondary:hover { background: var(--clam-surface-soft, #f5f6f8); }

        .cards-grid { display: grid; grid-template-columns: 1fr; gap: var(--clam-space-3, 12px); padding: 0 var(--clam-space-4, 16px) var(--clam-space-5, 20px); align-items: start; }
        .card { border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-lg, 12px); background: var(--clam-surface, #fff); box-shadow: var(--clam-shadow-sm, 0 1px 3px rgba(0,0,0,0.05)); display: flex; flex-direction: column; overflow: hidden; }
        .card__head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--clam-space-2, 8px); padding: var(--clam-space-3, 12px) var(--clam-space-4, 16px); }
        .card__title { font-size: var(--clam-text-base, 14px); font-weight: 700; color: var(--clam-text, #333); margin: 0; word-break: break-word; }
        .card__sub { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); margin: 3px 0 0; word-break: break-all; }
        .card__body { padding: 0 var(--clam-space-4, 16px) var(--clam-space-2, 8px); display: flex; gap: var(--clam-space-2, 8px); flex-wrap: wrap; align-items: center; }
        .card__foot { display: flex; gap: var(--clam-space-2, 8px); padding: var(--clam-space-2, 8px) var(--clam-space-4, 16px) var(--clam-space-3, 12px); border-top: 1px solid var(--clam-border, #e2e8f0); flex-wrap: wrap; }

        .badge { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; padding: 2px 8px; border-radius: 999px; display: inline-flex; align-items: center; gap: 4px; }
        .badge--on { background: #dcfce7; color: #166534; }
        .badge--off { background: #fee2e2; color: #b91c1c; }
        .badge--warn { background: #fef3c7; color: #92400e; }
        .badge--org { background: #e8f0fe; color: #1e40af; }
        .role-chip { background: #e8f5e0; color: #3f7a12; border-radius: 999px; padding: 2px 9px; font-size: var(--clam-text-xs, 11px); }

        .empty { text-align: center; padding: var(--clam-space-8, 40px); color: var(--clam-light-gray, #898a8c); font-size: var(--clam-text-base, 14px); }
        .loading { text-align: center; padding: var(--clam-space-8, 40px); color: var(--clam-light-gray, #898a8c); display: flex; align-items: center; justify-content: center; gap: 8px; }
        .spinner { display: inline-block; width: 15px; height: 15px; border: 2px solid var(--clam-border, #e2e8f0); border-top-color: var(--clam-primary, #5a9d1e); border-radius: 50%; animation: ccum-spin 0.7s linear infinite; }
        @keyframes ccum-spin { to { transform: rotate(360deg); } }

        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: flex-end; justify-content: center; z-index: 200; }
        .modal { background: var(--clam-surface, #fff); border-radius: var(--clam-radius-lg, 12px) var(--clam-radius-lg, 12px) 0 0; width: 100%; max-width: 480px; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 -8px 40px rgba(0,0,0,0.2); }
        .modal__header { display: flex; align-items: center; justify-content: space-between; gap: var(--clam-space-3, 12px); padding: var(--clam-space-4, 16px) var(--clam-space-5, 20px); border-bottom: 1px solid var(--clam-border, #e2e8f0); }
        .modal__header h2 { margin: 0; color: var(--clam-darkgray, #424244); font-size: var(--clam-text-lg, 18px); }
        .modal__close { width: 32px; height: 32px; border-radius: 50%; border: none; cursor: pointer; background: transparent; color: var(--clam-text-muted, #6d6e71); font-size: 18px; line-height: 1; display: inline-flex; align-items: center; justify-content: center; }
        .modal__close:hover { background: var(--clam-surface-soft, #f0f2f5); }
        .modal__body { padding: var(--clam-space-4, 16px); display: flex; flex-direction: column; gap: var(--clam-space-4, 16px); }
        .modal__footer { padding: var(--clam-space-3, 12px) var(--clam-space-4, 16px); border-top: 1px solid var(--clam-border, #e2e8f0); }
        .modal-actions { display: flex; gap: var(--clam-space-3, 12px); justify-content: flex-end; }
        .form-group { display: flex; flex-direction: column; }
        .form-group label { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); margin-bottom: var(--clam-space-1, 4px); font-weight: 500; }
        .form-group input { width: 100%; padding: 9px 10px; border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 6px); font-size: var(--clam-text-sm, 13px); font-family: var(--clam-font); box-sizing: border-box; }
        .form-group input:focus { outline: none; border-color: var(--clam-light-green, #6bbb24); }
        .muted { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); line-height: 1.4; }

        .toast { position: fixed; left: 50%; bottom: 24px; transform: translateX(-50%); z-index: 300; padding: 12px 20px; border-radius: var(--clam-radius-md, 8px); font-size: var(--clam-text-sm, 13px); font-weight: 600; box-shadow: 0 6px 20px rgba(0,0,0,0.18); max-width: 90vw; }
        .toast--ok { background: #dcfce7; color: #3f7a12; }
        .toast--err { background: #fee2e2; color: #b91c1c; }

        .org-banner { margin: 0 var(--clam-space-4, 16px) var(--clam-space-4, 16px); padding: var(--clam-space-4, 16px); border-radius: var(--clam-radius-md, 8px); font-size: var(--clam-text-sm, 13px); line-height: 1.5; display: flex; gap: var(--clam-space-3, 12px); align-items: flex-start; }
        .org-banner b { display: block; margin-bottom: 4px; font-size: var(--clam-text-base, 14px); font-weight: 700; }
        .org-banner--err { background: #fef3c7; color: #92400e; border: 1px solid #fcd34d; }
        .org-banner--ok { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; }
        .org-banner__icon { font-size: 18px; line-height: 1; flex-shrink: 0; }

        @media (min-width: 640px) {
            .toolbar { padding: var(--clam-space-5, 20px) var(--clam-space-6, 24px); }
            .cards-grid { grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); padding: 0 var(--clam-space-6, 24px) var(--clam-space-6, 24px); gap: var(--clam-space-4, 16px); }
            .modal-overlay { align-items: center; padding: var(--clam-space-4, 16px); }
            .modal { border-radius: var(--clam-radius-lg, 12px); }
            .modal__body { padding: var(--clam-space-5, 20px); }
        }
    `;

    private _users: AdminUser[] = [];
    private _loading = false;
    private _creating = false;
    private _createEmail = '';
    private _createName = '';
    private _createFamilyName = '';
    private _createPass = '';
    private _toast: Toast | null = null;
    // Estado de verificación de la org por defecto (env.MORPHED_ORG).
    // null = no verificado aún, '' = ok (id resuelto), '<mensaje>' = error.
    private _orgStatus: {ok: boolean; id: string; error: string} = {ok: false, id: '', error: ''};

    async connectedCallback(): Promise<void> {
        super.connectedCallback();
        await this._verifyOrg();
        if (this._orgStatus.ok) await this._refresh();
    }

    /** Verifica que la org por defecto (env.MORPHED_ORG) exista antes de permitir
     *  cualquier operación. Bloquea el componente si no está configurada. */
    private async _verifyOrg(): Promise<void> {
        try {
            const orgId = await resolveOrgId(true); // force=true para no usar cache stale
            if (orgId) {
                this._orgStatus = {ok: true, id: orgId, error: ''};
            } else {
                this._orgStatus = {
                    ok: false,
                    id: '',
                    error: `No se encontró la organización "${env.MORPHED_ORG}" en morphed-organization. Verifica que exista o contacta al administrador.`,
                };
            }
        } catch (e) {
            this._orgStatus = {ok: false, id: '', error: `Error al verificar la organización: ${String(e)}`};
        }
        this.requestUpdate();
    }

    private _toast_(kind: Toast['kind'], text: string): void {
        this._toast = {kind, text};
        this.requestUpdate();
        setTimeout(() => { this._toast = null; this.requestUpdate(); }, 3200);
    }

    private async _refresh(): Promise<void> {
        this._loading = true; this.requestUpdate();
        try {
            this._users = await loadAdminUsers();
        } catch (e) {
            this._toast_('err', `No se pudieron cargar los usuarios: ${String(e)}`);
            this._users = [];
        } finally {
            this._loading = false; this.requestUpdate();
        }
    }

    private async _action(path: string, method: string, body: Record<string, unknown>, okMsg: string): Promise<void> {
        try {
            const res = await authedFetch(`${env.MORPHED_API_URL}${path}`, {method, body: JSON.stringify(body), audience: 'morphed'});
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.error || `HTTP ${res.status}`);
            }
            this._toast_('ok', okMsg);
            await this._refresh();
        } catch (e) { this._toast_('err', `Error: ${String(e)}`); }
        this.requestUpdate();
    }

    private _openCreate(): void { this._creating = true; this._createEmail = ''; this._createName = ''; this._createFamilyName = ''; this._createPass = ''; this.requestUpdate(); }
    private _closeCreate(): void { this._creating = false; this.requestUpdate(); }

    private async _createUser(e: Event): Promise<void> {
        e.preventDefault();
        // Defense in depth: si la org no está verificada, abortar.
        if (!this._orgStatus.ok) {
            this._toast_('err', 'La organización por defecto no está verificada. No se puede crear el usuario.');
            return;
        }
        const email = this._createEmail.trim();
        const name = this._createName.trim();
        const familyName = this._createFamilyName.trim();
        if (!email) { this._toast_('err', 'El email es obligatorio.'); return; }
        if (!name || !familyName) { this._toast_('err', 'Nombre y apellidos son obligatorios.'); return; }
        this._creating = false; this.requestUpdate();
        try {
            // 1) Crear en Cognito con name + family_name (devuelve sub)
            const res = await authedFetch(`${env.MORPHED_API_URL}/auth/user`, {
                method: 'POST',
                body: JSON.stringify({
                    email, username: email,
                    attributes: {name, family_name: familyName},
                    ...(this._createPass ? {temporaryPassword: this._createPass} : {}),
                }),
                audience: 'morphed',
            });
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.error || `HTTP ${res.status}`);
            }
            const data = await res.json();
            // 2) Crear morphed_user vinculado (id = sub) con name + family_name + email + org verificada
            const sub: string = data.sub || '';
            if (sub) {
                await morphedCreate('morphed-user', [{
                    id: sub, email, name, family_name: familyName, role: 'agent', status: 'invited',
                    authProvider: 'cognito', authProviderId: sub,
                    organizations: [this._orgStatus.id], // org ya verificada, siempre presente
                }]);
            }
            this._toast_('ok', `Usuario ${email} creado y enlazado a ${env.MORPHED_ORG}.`);
            await this._refresh();
        } catch (err) {
            this._toast_('err', `Error: ${String(err)}`);
        }
        this.requestUpdate();
    }

    private _rolesOf(u: AdminUser): string[] {
        const out: string[] = [];
        for (const r of rolesSignal.value) {
            if ((r.users || []).includes(u.id)) out.push(r.name);
        }
        return out;
    }

    render() {
        return html`
            <div class="toolbar">
                <div class="toolbar__left">
                    <h1 class="toolbar__title">Usuarios</h1>
                    <p class="toolbar__subtitle">Organización <b>${env.MORPHED_ORG}</b> · ${this._users.length} usuario(s) · las acciones requieren ser admin</p>
                </div>
                <div class="toolbar__right">
                    <button class="btn btn-secondary" @click=${() => this._verifyOrg().then(() => { if (this._orgStatus.ok) void this._refresh(); })} ?disabled=${this._loading}>${this._loading ? html`<span class="spinner"></span>` : ''} Refrescar</button>
                    <button class="btn btn-primary" @click=${() => this._openCreate()} ?disabled=${!this._orgStatus.ok} title=${this._orgStatus.ok ? '' : 'Organización no verificada'}>+ Nuevo Usuario</button>
                </div>
            </div>

            ${!this._orgStatus.ok ? html`
                <div class="org-banner org-banner--err">
                    <span class="org-banner__icon">⚠</span>
                    <div>
                        <b>Organización por defecto no verificada</b>
                        ${this._orgStatus.error}
                        <button class="btn btn-secondary btn-sm" style="margin-top:8px" @click=${() => this._verifyOrg()}>Reintentar verificación</button>
                    </div>
                </div>
            ` : this._loading && this._users.length === 0
                ? html`<div class="loading"><span class="spinner"></span> Cargando usuarios...</div>`
                : this._users.length === 0
                    ? html`<div class="empty">Sin usuarios en la organización <b>${env.MORPHED_ORG}</b>. Crea el usuario y luego enlázalo a esta org.</div>`
                    : html`<div class="cards-grid">${this._users.map((u) => {
                        const roles = this._rolesOf(u);
                        const uname = u.cognitoUsername;
                        return html`
                        <div class="card">
                            <div class="card__head">
                                <div>
                                    <h3 class="card__title">${u.name || u.email}</h3>
                                    <p class="card__sub">${u.email || '—'} · sub: ${u.id.slice(0, 8)}…</p>
                                </div>
                                <div style="display:flex;flex-direction:column;gap:4px;align-items:flex-end">
                                    ${u.enabled === false ? html`<span class="badge badge--off">● deshab.</span>` : html`<span class="badge badge--on">● activo</span>`}
                                    ${u.cognitoStatus && u.cognitoStatus !== 'CONFIRMED' ? html`<span class="badge badge--warn">${u.cognitoStatus}</span>` : ''}
                                </div>
                            </div>
                            <div class="card__body">
                                <span class="badge badge--org">${env.MORPHED_ORG}</span>
                                ${roles.length ? roles.map((rl) => html`<span class="role-chip">${rl}</span>`) : html`<span class="muted">sin rol</span>`}
                            </div>
                            <div class="card__foot">
                                ${uname ? html`
                                    ${u.enabled === false
                                        ? html`<button class="btn btn-secondary btn-sm" @click=${() => this._action('/auth/user/enable', 'POST', {username: uname}, `${u.email} habilitado`)}>Habilitar</button>`
                                        : html`<button class="btn btn-secondary btn-sm" @click=${() => this._action('/auth/user/disable', 'POST', {username: uname}, `${u.email} deshabilitado`)}>Deshabilitar</button>`}
                                    <button class="btn btn-secondary btn-sm" @click=${() => this._action('/auth/password-reset', 'POST', {username: uname}, `Reset solicitado para ${u.email}`)}>Reset pass</button>
                                    <button class="btn btn-danger btn-sm" @click=${() => { if (confirm(`¿Eliminar ${u.email} de Cognito?`)) void this._action('/auth/user', 'DELETE', {username: uname}, `${u.email} eliminado`); }}>Eliminar</button>`
                                : html`<span class="muted">No encontrado en Cognito (enlaza manualmente)</span>`}
                            </div>
                        </div>`;
                    })}</div>`}

            ${this._creating ? this._renderCreate() : ''}
            ${this._toast ? html`<div class="toast toast--${this._toast.kind}">${this._toast.text}</div>` : ''}
        `;
    }

    private _renderCreate() {
        return html`
            <div class="modal-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this._closeCreate(); }}>
                <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
                    <form @submit=${(e: SubmitEvent) => this._createUser(e)}>
                        <div class="modal__header">
                            <h2>Nuevo usuario</h2>
                            <button type="button" class="modal__close" title="Cerrar" @click=${() => this._closeCreate()}>×</button>
                        </div>
                        <div class="modal__body">
                            <div class="form-group">
                                <label>Nombre</label>
                                <input type="text" placeholder="Nombre" .value=${this._createName} @input=${(e: Event) => { this._createName = (e.target as HTMLInputElement).value; this.requestUpdate(); }} required>
                            </div>
                            <div class="form-group">
                                <label>Apellidos</label>
                                <input type="text" placeholder="Apellidos" .value=${this._createFamilyName} @input=${(e: Event) => { this._createFamilyName = (e.target as HTMLInputElement).value; this.requestUpdate(); }} required>
                            </div>
                            <div class="form-group">
                                <label>Email</label>
                                <input type="email" placeholder="usuario@dominio.com" .value=${this._createEmail} @input=${(e: Event) => { this._createEmail = (e.target as HTMLInputElement).value; this.requestUpdate(); }} required>
                            </div>
                            <div class="form-group">
                                <label>Contraseña temporal (opcional)</label>
                                <input type="text" placeholder="Déjalo vacío para que Cognito genere y envíe por email" .value=${this._createPass} @input=${(e: Event) => { this._createPass = (e.target as HTMLInputElement).value; this.requestUpdate(); }}>
                                <span class="muted">Se crea en Cognito y se enlaza automáticamente a la org <b>${env.MORPHED_ORG}</b> (morphed_user con id = sub).</span>
                            </div>
                        </div>
                        <div class="modal__footer">
                            <div class="modal-actions">
                                <button type="button" class="btn btn-secondary" @click=${() => this._closeCreate()}>Cancelar</button>
                                <button type="submit" class="btn btn-primary">Crear usuario</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>`;
    }
}

customElements.define('clam-user-manager', ClamUserManager);

declare global {
    interface HTMLElementTagNameMap {
        'clam-user-manager': ClamUserManager;
    }
}
